import {useEffect, useState} from "react";
import ErrorHelper from "../../../utils/ErrorHelper";
import {useFormGuard} from "@/utils/formGuard";
import {FormattedPageTitle} from "../../pages";
import {Checkbox, IconButton} from "@mui/material";
import {ErrorWithContext, TextField} from "@dvrt/graphql-widgets";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {defineMessage, FormattedMessage} from "react-intl";
import CallQueueSettingsForm, {FILE_FIELD_MESSAGES, FILE_FIELDS} from "./CallQueueSettingsForm";
import OutgoingCallBarringWidget from "../../../outgoing-call-barring/OutgoingCallBarringWidget";
import {formCheckboxMargin, formPagesWidth} from "@/constants";
import {MAX_I32, removeFileExtension} from "@/tools";
import {DataSource, useDataSource} from "./DataSource";
import Loading from "@/components/breadcrumbs/Loading";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {EnumField, NumberField} from "@dvrt/graphql-widgets";
import {PendingWrapper, FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {HuntingAlgorithmType} from "@/api";
import {CallQueueMedia} from "@/api";
import {saveAudio} from "@/tools";
import {useDispatch} from "react-redux";
import DomainMediaSelector, {DomainMediaSelectorOnChange} from "@/components/DomainMediaSelector";
import {HUNTING_ALGORITHM_TYPE_MESSAGES} from "@/utils/translations";

const Content = ({dataSource}: {dataSource: DataSource}) => {
    const auth = useAuth();
    const domainContextFilter = ContextFilters.domain(dataSource.domainName);
    const canEditAdmin = auth.hasPermission("edit_domain", domainContextFilter);
    const canEditOperator = auth.hasPermission("edit_superuser_group", domainContextFilter);
    const [unlimited, setUnlimited] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const form = new CallQueueSettingsForm(dataSource, canEditOperator);
    useFormGuard(form.hasChanged);

    useEffect(() => {
        setUnlimited(form.values.maxQueueLength === -1);
    }, [form.values.maxQueueLength]);

    const errorHelper = new ErrorHelper(form.errors);

    async function submit() {
        setSubmitting(true);
        await form.submit();
        setSubmitting(false);
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "callGroupSettings", defaultMessage: "Settings for call group {name}"})}
                values={{name: form.values.name}}
            />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})}>
                    <TextField
                        id="name"
                        disabled={!canEditAdmin}
                        value={form.values.name}
                        onChange={(value) => {
                            form.updateValues({name: value});
                        }}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "sequentialTimeout", defaultMessage: "Ringing timeout (seconds)"})}
                >
                    <NumberField
                        disabled={!canEditAdmin}
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        value={form.values.sequentialTimeout}
                        max={90}
                        min={0}
                        update={(newValue) => form.updateValues({sequentialTimeout: newValue})}
                        errors={errorHelper.fieldErrors("sequentialTimeout")}
                        style={{margin: 0}}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "hasAddressBook", defaultMessage: "Address book available"})}
                >
                    <Checkbox
                        disabled={!canEditAdmin}
                        id="hasAddressBook"
                        checked={form.values.hasAddressBook}
                        onChange={(event) => form.updateValues({hasAddressBook: event.target.checked})}
                        color="primary"
                        style={{marginLeft: formCheckboxMargin}}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "huntingAlgorithm", defaultMessage: "Call distribution"})}>
                    <EnumField
                        disabled={!canEditAdmin}
                        value={form.values.huntingAlgorithmType}
                        enum_={{HuntingAlgorithmType}}
                        onChange={(huntingAlgorithmType) => form.updateValues({huntingAlgorithmType})}
                        translations={HUNTING_ALGORITHM_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "maxQueueLength", defaultMessage: "Maximum waiting calls"})}
                >
                    <NumberField
                        id="maxQueueLength"
                        disabled={!canEditAdmin || unlimited}
                        value={form.values.maxQueueLength}
                        fullWidth={false}
                        update={(value) => {
                            const newValue = Number(value);
                            form.updateValues({maxQueueLength: newValue >= 0 ? newValue : 0});
                        }}
                    />
                    <Checkbox
                        disabled={!canEditAdmin}
                        checked={unlimited}
                        onChange={(event) => {
                            setUnlimited(event.target.checked);
                            form.updateValues({maxQueueLength: event.target.checked ? -1 : 0});
                        }}
                        color="primary"
                    />
                    <FormattedMessage id="unlimited" defaultMessage="Unlimited" />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "maxWaitingTime", defaultMessage: "Maximum waiting time (seconds)"})}
                >
                    <NumberField
                        disabled={!canEditAdmin}
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        value={form.values.maxWaitingTime}
                        max={MAX_I32}
                        min={0}
                        update={(newValue) => form.updateValues({maxWaitingTime: newValue})}
                        errors={errorHelper.fieldErrors("maxWaitingTime")}
                        style={{margin: 0}}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "acwLength", defaultMessage: "After call work length"})}>
                    <NumberField
                        disabled={!canEditAdmin}
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        value={form.values.acwLength}
                        max={MAX_I32}
                        min={0}
                        update={(newValue) => form.updateValues({acwLength: newValue})}
                        errors={errorHelper.fieldErrors("acwLength")}
                        style={{margin: 0}}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "call_queue.settings.priority", defaultMessage: "Priority"})}
                >
                    <NumberField
                        disabled={!canEditAdmin}
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        value={form.values.priority}
                        max={99}
                        min={1}
                        update={(newValue) => form.updateValues({priority: newValue})}
                        errors={errorHelper.fieldErrors("priority")}
                        style={{margin: 0}}
                    />
                </FormattedFormRow>
                {FILE_FIELDS.map((field, index) => (
                    <FormattedFormRow
                        label={FILE_FIELD_MESSAGES[field]}
                        labelStyle={{paddingTop: null, alignSelf: "center"}}
                        key={index}
                    >
                        <MediaField
                            id={field}
                            field={form.values[field]}
                            defaultFilename={"queueAudio"}
                            downloadToken={dataSource.downloadTokens[form.values[field].id]}
                            domainId={dataSource.domainId}
                            onChange={(value) => {
                                let newValue: CallQueueMedia;
                                if (value === undefined)
                                    newValue = {
                                        id: undefined,
                                        file: undefined,
                                        description: undefined,
                                        unset: true,
                                    };
                                else if (value instanceof File)
                                    newValue = {
                                        id: undefined,
                                        unset: false,
                                        file: value,
                                        description: removeFileExtension(value.name),
                                    };
                                else
                                    newValue = {
                                        id: value.id,
                                        description: value.description,
                                        unset: false,
                                        file: undefined,
                                    };

                                form.updateValues({[field]: newValue});
                            }}
                            errors={errorHelper.fieldErrors(field)}
                        />
                    </FormattedFormRow>
                ))}
                <FormattedFormRow
                    label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                    labelAlign="top"
                >
                    <OutgoingCallBarringWidget
                        masterBarrings={dataSource.domainOutgoingCallBarrings}
                        operatorBarrings={form.values.outgoingCallBarringOperator}
                        adminBarrings={form.values.outgoingCallBarringAdmin}
                        updateOperatorBarrings={(barrings) =>
                            form.updateValues({outgoingCallBarringOperator: barrings})
                        }
                        updateAdminBarrings={(barrings) => form.updateValues({outgoingCallBarringAdmin: barrings})}
                        disabledAdmin={!canEditAdmin}
                        disabledOperator={!canEditOperator}
                    />
                </FormattedFormRow>
                {canEditAdmin && (
                    <FormattedFormRow>
                        <div style={{margin: "1em 0"}}>
                            <FormActionButtons
                                mainAction={submit}
                                secondaryAction={() => {
                                    form.reset();
                                }}
                                enabled={form.hasChanged && !submitting}
                                pending={submitting}
                            />
                        </div>
                    </FormattedFormRow>
                )}
            </FormContainer>
        </div>
    );
};

type Props = {
    domainId: number;
    groupId: number;
};

export default (props: Props) => {
    const dataSource = useDataSource(props.domainId, props.groupId);

    if (dataSource.loading) return <Loading />;

    return <Content dataSource={dataSource} />;
};

type MediaFieldProps = {
    id: any;
    field: CallQueueMedia;
    downloadToken: string | undefined;
    defaultFilename: string;
    onChange: DomainMediaSelectorOnChange;
    domainId: number;
    errors?: ErrorWithContext[];
};

/**
 * A component for managing a media field in the call queue settings form.
 *
 * @param id ID for the component
 * @param field The field to manage.
 * @param dataSourceMedia The media data from the data source. Contains information about the specific media field.
 * @param form The form to update.
 * @param notSelectedText The text to display when no media is selected/set.
 * @param saveAudio A function for downloading audio files.
 * @param defaultFilename The default filename to use when there was no description set.
 * @param accessToken The access token to use for the request.
 * @param error The error message to display.
 */
const MediaField = ({id, field, downloadToken, defaultFilename, onChange, domainId, errors}: MediaFieldProps) => {
    const dispatch = useDispatch();
    const auth = useAuth();

    const [downloading, setDownloading] = useState(false);
    const mediaDescription = field.file?.name?.split(".").slice(0, -1).join(".") ?? field.description;

    console.log("media field", field, downloadToken);

    const handleDownload = async () => {
        setDownloading(true);
        await saveAudio(downloadToken, mediaDescription ?? defaultFilename, auth.accessToken, dispatch);
        setDownloading(false);
    };

    const DownloadButton = () => {
        return (
            <PendingWrapper pending={downloading}>
                <IconButton onClick={handleDownload} color="primary" size="large">
                    <FontAwesomeIcon icon="download" />
                </IconButton>
            </PendingWrapper>
        );
    };

    return (
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}} id={id}>
            <div>
                <DomainMediaSelector
                    domainId={domainId}
                    value={field.unset ? undefined : (field.id ?? field.file)}
                    onChange={onChange}
                    errors={errors}
                />
            </div>
            <div>
                {downloadToken && <DownloadButton />}
                {(field.id || field.file) && !field.unset && (
                    <IconButton
                        component="label"
                        color="primary"
                        onClick={() => onChange(undefined)}
                        disabled={downloading}
                        size="large"
                    >
                        <FontAwesomeIcon icon="times" />
                    </IconButton>
                )}
            </div>
        </div>
    );
};
